import React from 'react';
import { Link } from 'react-router-dom';
import { Car, UserCog, Plane, Camera, Building2, Handshake, ArrowRight } from 'lucide-react';

const SERVICES = [
  { slug: 'booking-request', icon: Car, title: 'Book a Rental', desc: 'Self-drive exotic & luxury vehicles', accent: 'red' },
  { slug: 'chauffeur-request', icon: UserCog, title: 'Request Chauffeur', desc: 'Professional driver for any occasion', accent: 'red' },
  { slug: 'airport-transfer', icon: Plane, title: 'Airport Transfer', desc: 'Seamless arrivals & departures', accent: 'red' },
  { slug: 'event-booking', icon: Camera, title: 'Event / Photoshoot', desc: 'Production, content & events', accent: 'red' },
  { slug: 'owner-onboarding', icon: Building2, title: 'List Your Vehicle', desc: 'Monetize your asset with ACE', accent: 'green' },
  { slug: 'broker-submission', icon: Handshake, title: 'Broker / Affiliate', desc: 'Submit clients & earn commissions', accent: 'green' },
];

export default function ServicesHub() {
  return (
    <div className="bg-stone-50 min-h-screen pb-16" data-testid="services-hub">
        {/* Hero */}
        <div className="bg-black text-white">
          <div className="max-w-5xl mx-auto px-5 py-16 sm:py-20 text-center">
            <p className="text-[10px] uppercase tracking-[0.35em] text-red-500 font-bold">ACE · Auto Concierge</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-4">What are you looking for?</h1>
            <p className="text-base text-gray-300 mt-5 max-w-2xl mx-auto">
              Luxury rentals, chauffeured service, exotic fleet access — or partnership opportunities. Pick a path below and our team will reach out within hours.
            </p>
          </div>
        </div>

        {/* Service cards */}
        <div className="max-w-6xl mx-auto px-5 -mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map(s => (
              <Link
                key={s.slug}
                to={`/intake/${s.slug}`}
                className="group bg-white border border-gray-200 hover:border-red-600 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl"
                data-testid={`service-card-${s.slug}`}
              >
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-4 ${s.accent === 'red' ? 'bg-red-600/10 group-hover:bg-red-600' : 'bg-green-600/10 group-hover:bg-green-600'}`}>
                  <s.icon className={`h-6 w-6 ${s.accent === 'red' ? 'text-red-600 group-hover:text-white' : 'text-green-700 group-hover:text-white'} transition-colors`} />
                </div>
                <h3 className="text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{s.desc}</p>
                <div className="flex items-center gap-1 mt-4 text-xs font-bold text-red-600 group-hover:underline">
                  Submit Request <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>

          {/* How it works */}
          <div className="mt-16">
            <h2 className="text-center text-2xl font-black tracking-tight mb-8">How It Works</h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              {[
                { n: '01', t: 'Submit Request', d: 'Pick a service, fill out the intake form. No account needed.' },
                { n: '02', t: 'Verification', d: 'Our team reviews your ID, insurance, and request within hours.' },
                { n: '03', t: 'Confirmation', d: 'We confirm dates, delivery, deposit, and special requirements.' },
                { n: '04', t: 'Experience', d: 'Vehicle delivered or ready for pickup. Your concierge is on standby.' },
              ].map(step => (
                <div key={step.n} className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-xs uppercase tracking-widest text-red-600 font-bold">{step.n}</p>
                  <h3 className="text-base font-bold tracking-tight mt-2">{step.t}</h3>
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">{step.d}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 text-center mt-5">
              All bookings are request-based. Instant booking is not available. All rentals require approval.
            </p>
          </div>
        </div>
    </div>
  );
}
